import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const STORAGE_KEY = "ir_cookie_consent_v1";

const translations = {
  title: { es: "Usamos cookies", en: "We use cookies" },
  body: {
    es: "Utilizamos cookies esenciales para el funcionamiento del sitio y, con tu consentimiento, cookies analíticas y de marketing para mejorar tu experiencia. Puedes aceptar todas, rechazar las opcionales o revisar nuestra política.",
    en: "We use essential cookies for site functionality and, with your consent, analytics and marketing cookies to improve your experience. You can accept all, reject optional ones, or review our policy.",
  },
  accept: { es: "Aceptar todas", en: "Accept all" },
  rejectOptional: { es: "Solo esenciales", en: "Essentials only" },
  policy: { es: "Política de cookies", en: "Cookie policy" },
  close: { es: "Cerrar", en: "Close" },
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Delay to avoid intrusive flash on first load
      const timer = setTimeout(() => setVisible(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  const persist = (consent) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ consent, timestamp: new Date().toISOString() })
    );
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[70]"
        >
          <div className="relative bg-[#0B1F3B] border border-white/10 rounded-2xl shadow-2xl p-6 backdrop-blur">
            <button
              onClick={() => persist({ essential: true, analytics: false, marketing: false })}
              data-testid="cookie-close"
              aria-label={t(translations.close)}
              className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 bg-[#F59E0B]/20 rounded-full flex items-center justify-center shrink-0">
                <Cookie className="w-4 h-4 text-[#F59E0B]" />
              </div>
              <h3 className="font-display text-white text-lg font-medium leading-tight pt-1">
                {t(translations.title)}
              </h3>
            </div>

            <p className="font-body text-white/70 text-sm leading-relaxed mb-5">
              {t(translations.body)}{" "}
              <Link
                to="/legal/cookies"
                className="text-[#2ED3C6] hover:text-[#F59E0B] underline underline-offset-2 transition-colors"
                data-testid="cookie-policy-link"
              >
                {t(translations.policy)}
              </Link>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => persist({ essential: true, analytics: false, marketing: false })}
                data-testid="cookie-reject-optional"
                className="flex-1 px-4 py-2.5 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-body text-sm font-semibold transition-all"
              >
                {t(translations.rejectOptional)}
              </button>
              <button
                onClick={() => persist({ essential: true, analytics: true, marketing: true })}
                data-testid="cookie-accept-all"
                className="flex-1 px-4 py-2.5 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-body text-sm font-semibold transition-all hover:-translate-y-0.5"
              >
                {t(translations.accept)}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
